// src/components/molecules/PortalUserModal/PortalUserModal.tsx
import React, { useMemo } from 'react';
import { Container, Row, Col, Form } from 'shared-portal-component';
import { z } from 'zod';

import AppModal from '@/components/common/AppModal/AppModal';
import type { PortalUserValues } from '@/types/users.types';

interface PortalUserModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: PortalUserValues) => void;

  title: string;
  submitText?: string;
  modalSize?: 'sm' | 'md' | 'lg';
  className?: string;

  initialValues?: Partial<PortalUserValues>;
  deptOrgOptions?: string[];
}

/* ============================
   Schema
============================ */

const MIN_LENGTH = 1;
const portalUserSchema = z.object({
  userType: z.enum(['Internal', 'External']),
  name: z.string().min(MIN_LENGTH, 'Please enter a full name'),
  email: z
    .string()
    .min(MIN_LENGTH, 'Email is required')
    .email('Please enter a valid email'),
  phone: z.string().min(MIN_LENGTH, 'Phone number is required'),
  deptOrg: z.string().min(MIN_LENGTH, 'Please select a department / org'),
  sendInvite: z.boolean().default(true),
  forcePwdReset: z.boolean().default(true),
});

/* ============================
   Defaults
============================ */
const DEFAULT_SUBMIT = 'Save';
const DEFAULT_VALUES: PortalUserValues = {
  userType: 'Internal',
  name: '',
  email: '',
  phone: '',
  deptOrg: '',
  sendInvite: true,
  forcePwdReset: true,
};
const DEFAULT_DEPTS = ['HR', 'IT', 'Delivery', 'Legal', 'Sales'];

const mergeDefaults = (seed?: Partial<PortalUserValues>): PortalUserValues => ({
  ...DEFAULT_VALUES,
  ...(seed ?? {}),
});

/* ============================
   Component
============================ */
const PortalUserModal: React.FC<PortalUserModalProps> = ({
  open,
  onClose,
  onSubmit,
  title,
  submitText = DEFAULT_SUBMIT,
  modalSize = 'sm',
  className = '',
  initialValues,
  deptOrgOptions = DEFAULT_DEPTS,
}) => {
  const defaults = useMemo(() => mergeDefaults(initialValues), [initialValues]);

  const fields = useMemo(
    () => [
      {
        name: 'userType',
        label: 'Is this user Internal or External?',
        type: 'radio' as const,
        options: [
          { label: 'Internal', value: 'Internal' },
          { label: 'External', value: 'External' },
        ],
      },
      {
        name: 'name',
        label: 'User Name',
        placeholder: 'Full name',
        required: true,
        type: 'text' as const,
      },
      {
        name: 'email',
        label: 'User Email',
        placeholder: 'user@company.com',
        required: true,
        type: 'email' as const,
      },
      {
        name: 'phone',
        label: 'Phone Number',
        placeholder: '(555) 123-4567',
        required: true,
        type: 'phone' as const,
      },
      {
        name: 'deptOrg',
        label: 'Dept / Org',
        placeholder: 'Select department / org',
        required: true,
        type: 'select' as const,
        options: deptOrgOptions.map((d) => ({ label: d, value: d })),
      },
    ],
    [deptOrgOptions]
  );

  return open ? (
    <AppModal
      onClose={onClose}
      title={title}
      size={modalSize}
      className={className}
      aria-label={title}
    >
      <Container fluid>
        <Row spacing="none" justify="center">
          <Col>
            <Form
              schema={portalUserSchema}
              defaultValues={defaults}
              onSubmit={onSubmit}
              submitText={submitText}
              fields={fields}
            />
          </Col>
        </Row>
      </Container>
    </AppModal>
  ) : null;
};

export default PortalUserModal;
